import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Modal, Row, Table } from "react-bootstrap";
import { collection, collectionGroup, getDocs } from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import PageTitle from "../../layouts/PageTitle";

import { filterCpapFollowupRecords, getCpapFollowupRole, isCpapFollowupNodeCustomerScopedUser } from "./cpapFollowupAccess";
import { loadCpapFollowupScope } from "./cpapFollowupDataAccess";

const PAGE_SIZE = 50;

const formatDateTime = (value) => {
  if (!value) return "-";

  let date = value;

  if (value && typeof value.toDate === "function") {
    date = value.toDate();
  } else if (
    value &&
    typeof value === "object" &&
    typeof value.seconds === "number"
  ) {
    date = new Date(value.seconds * 1000);
  } else {
    date = new Date(value);
  }

  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getTimestamp = (value) => {
  if (!value) return 0;
  if (value && typeof value.toDate === "function") {
    return value.toDate().getTime();
  }
  if (
    value &&
    typeof value === "object" &&
    typeof value.seconds === "number"
  ) {
    return value.seconds * 1000;
  }

  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const getPatientName = (row) =>
  [row.firstname, row.lastname].filter(Boolean).join(" ") || "-";

const getRecordValue = (row, key) => {
  if (row && row[key] !== undefined && row[key] !== null && row[key] !== "") {
    return row[key];
  }

  const ep = (row && row.extendedProps) || {};
  return ep[key] !== undefined && ep[key] !== null && ep[key] !== ""
    ? ep[key]
    : "";
};

const getPreviousDecisionRecord = (row) => {
  if (!row) return null;

  const ep = row.extendedProps || {};
  const record =
    row.latestTherapyStatusRecord ||
    ep.latestTherapyStatusRecord ||
    row.latestTherapyStatus ||
    ep.latestTherapyStatus;

  return record && typeof record === "object" ? record : null;
};

const getDoctorActionLabel = (value) => {
  const actionMap = {
    "No Doctor Action": "รอการประเมินจากแพทย์",
    "Adjust Pressure": "ปรับ Pressure",
    "Doctor Appointment": "นัดพบแพทย์",
    "Doctor Phone Consult": "แพทย์โทรติดตามอาการ",
    "Continue Therapy": "ใช้เครื่องต่อ",
  };

  return actionMap[value] || value || "-";
};

const getHistoryActionLabel = (value) => {
  const actionMap = {
    create: "Create",
    update: "Update",
    doctor_action: "Doctor Action",
  };

  return actionMap[value] || value || "-";
};

const getHistoryActionBadge = (value) => {
  if (value === "create") return "success";
  if (value === "doctor_action") return "warning";
  if (value === "update") return "info";
  return "secondary";
};

const renderValue = (value) => {
  if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
  if (value && typeof value === "object") return JSON.stringify(value);
  return value || "-";
};

const DetailRow = ({ label, value }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "170px 1fr",
      gap: 12,
      padding: "6px 0",
      borderBottom: "1px solid #f0edf4",
    }}
  >
    <div className="text-muted">{label}</div>
    <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {renderValue(value)}
    </div>
  </div>
);

const DetailSection = ({ title, children }) => (
  <div className="mb-4">
    <div
      className="mb-2"
      style={{ fontWeight: 700, color: "#4b325d", fontSize: "1rem" }}
    >
      {title}
    </div>
    <div
      style={{
        border: "1px solid #ece8f3",
        borderRadius: 8,
        padding: "8px 14px",
        background: "#fff",
      }}
    >
      {children}
    </div>
  </div>
);

const FollowupRecordHistory = () => {
  const { currentUser } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("All Actions");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRecord, setSelectedRecord] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadRecords = async () => {
      setRecords([]);
      setSelectedRecord(null);
      if (!currentUser) { setLoading(false); return; }

      setLoading(true);

      try {
        const role = getCpapFollowupRole(currentUser);
        const userName = currentUser.displayName || "";
        const shouldFilterByNode = isCpapFollowupNodeCustomerScopedUser(currentUser);

        const [recordSnap, nodeSnap] = await Promise.all([
          getDocs(collectionGroup(db, "followupRecord")),
          shouldFilterByNode ? getDocs(collection(db, "nodes")) : null,
        ]);

        const allNodes = (nodeSnap ? nodeSnap.docs : []).map((row) => ({ ...row.data(), id: row.id }));
        const scope = await loadCpapFollowupScope(currentUser, allNodes, false);

        let rows = recordSnap.docs
          .map((item) => ({
            id: item.id,
            path: item.ref.path,
            ...item.data(),
          }));

        if (role === "sleep_physician") {
          rows = rows.filter(
            (row) => getRecordValue(row, "doctorConsult") === userName,
          );
        }
        rows = filterCpapFollowupRecords({ records: rows, currentUser, adminData: scope.adminData, nodes: scope.nodes });

        rows = rows.sort(
          (a, b) => getTimestamp(b.recordAt) - getTimestamp(a.recordAt),
        );

        if (mounted) setRecords(rows);
      } catch (error) {
        console.error("load followupRecord history error", error);
        if (mounted) setRecords([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadRecords();

    return () => {
      mounted = false;
    };
  }, [currentUser]);

  const filteredRecords = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return records.filter((row) => {
      if (actionFilter !== "All Actions" && row.action !== actionFilter) {
        return false;
      }

      if (!keyword) return true;

      const previousDecision = getPreviousDecisionRecord(row);
      const text = [
        row.order,
        row.hn,
        getPatientName(row),
        row.type,
        row.status,
        row.doctorActionStatus,
        getDoctorActionLabel(row.doctorActionStatus),
        row.technicianConsult,
        row.doctorConsult,
        row.serialNumber,
        row.sideEffectsOther,
        getRecordValue(row, "sideEffectsOther"),
        row.note,
        row.recordBy,
        previousDecision && previousDecision.date,
        previousDecision && previousDecision.type,
        previousDecision && previousDecision.status,
        previousDecision && previousDecision.minPressure,
        previousDecision && previousDecision.maxPressure,
        previousDecision && previousDecision.currentRampTime,
        previousDecision && previousDecision.doctorActionStatus,
        previousDecision &&
          getDoctorActionLabel(previousDecision.doctorActionStatus),
        previousDecision && previousDecision.note,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return text.includes(keyword);
    });
  }, [records, search, actionFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredRecords.length / PAGE_SIZE),
  );
  const activePage = Math.min(currentPage, totalPages);
  const startIndex = (activePage - 1) * PAGE_SIZE;
  const paginatedRecords = filteredRecords.slice(
    startIndex,
    startIndex + PAGE_SIZE,
  );
  const startItem = filteredRecords.length === 0 ? 0 : startIndex + 1;
  const endItem = Math.min(startIndex + PAGE_SIZE, filteredRecords.length);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const renderDetailModal = () => {
    if (!selectedRecord) return null;

    const row = selectedRecord;
    const ep = row.extendedProps || {};
    const usageReports = Array.isArray(ep.usageReports)
      ? ep.usageReports
      : [];
    const questionnaires = Array.isArray(ep.questionnaires)
      ? ep.questionnaires
      : [];
    const sideEffects = Array.isArray(row.sideEffects)
      ? row.sideEffects
      : Array.isArray(ep.sideEffects)
        ? ep.sideEffects
        : [];
    const previousDecision = getPreviousDecisionRecord(row);

    return (
      <Modal
        show={!!selectedRecord}
        onHide={() => setSelectedRecord(null)}
        centered
        size="xl"
      >
        <Modal.Header>
          <Modal.Title>
            {getRecordValue(row, "order") || "Follow-Up Record"}
          </Modal.Title>
          <Button
            variant=""
            className="close"
            onClick={() => setSelectedRecord(null)}
          >
            <span>&times;</span>
          </Button>
        </Modal.Header>
        <Modal.Body style={{ background: "#fbfafc" }}>
          <Row>
            <Col lg={6}>
              <DetailSection title="Patient">
                <DetailRow label="HN" value={getRecordValue(row, "hn")} />
                <DetailRow label="First Name" value={getRecordValue(row, "firstname")} />
                <DetailRow label="Last Name" value={getRecordValue(row, "lastname")} />
                <DetailRow label="Phone" value={getRecordValue(row, "phone")} />
                <DetailRow label="Node" value={getRecordValue(row, "node")} />
                <DetailRow label="Customer" value={getRecordValue(row, "customer")} />
              </DetailSection>
            </Col>
            <Col lg={6}>
              <DetailSection title="Follow-Up">
                <DetailRow label="Action" value={getHistoryActionLabel(row.action)} />
                <DetailRow label="Record At" value={formatDateTime(row.recordAt)} />
                <DetailRow label="Record By" value={row.recordBy} />
                <DetailRow label="Follow-Up UUID" value={row.followupUuid} />
                <DetailRow label="Date" value={getRecordValue(row, "date") || formatDateTime(row.start)} />
                <DetailRow label="Type" value={getRecordValue(row, "type")} />
                <DetailRow label="Status" value={getRecordValue(row, "status")} />
                <DetailRow
                  label="Doctor Action"
                  value={getDoctorActionLabel(
                    getRecordValue(row, "doctorActionStatus"),
                  )}
                />
              </DetailSection>
            </Col>
          </Row>

          {previousDecision && (
            <Row>
              <Col lg={12}>
                <DetailSection title="Previous Decision">
                  <DetailRow label="Date" value={previousDecision.date} />
                  <DetailRow label="Type" value={previousDecision.type} />
                  <DetailRow label="Status" value={previousDecision.status} />
                  <DetailRow
                    label="Min Pressure"
                    value={previousDecision.minPressure}
                  />
                  <DetailRow
                    label="Max Pressure"
                    value={previousDecision.maxPressure}
                  />
                  <DetailRow
                    label="Current Ramp Time"
                    value={previousDecision.currentRampTime}
                  />
                  <DetailRow
                    label="Doctor Action"
                    value={getDoctorActionLabel(
                      previousDecision.doctorActionStatus,
                    )}
                  />
                  <DetailRow label="Note" value={previousDecision.note} />
                </DetailSection>
              </Col>
            </Row>
          )}

          <Row>
            <Col lg={6}>
              <DetailSection title="CPAP Device">
                <DetailRow label="Serial Number" value={getRecordValue(row, "serialNumber")} />
                <DetailRow label="Brand" value={getRecordValue(row, "brand")} />
                <DetailRow label="Model" value={getRecordValue(row, "model")} />
                <DetailRow label="CPAP Type" value={getRecordValue(row, "cpapType")} />
                <DetailRow label="Mask Type" value={getRecordValue(row, "maskType")} />
                <DetailRow label="Mask Size" value={getRecordValue(row, "maskSize")} />
                <DetailRow label="Min Pressure" value={getRecordValue(row, "minPressure")} />
                <DetailRow label="Max Pressure" value={getRecordValue(row, "maxPressure")} />
                <DetailRow label="Ramp Time" value={getRecordValue(row, "currentRampTime")} />
                <DetailRow label="EPR" value={getRecordValue(row, "epr")} />
                <DetailRow
                  label="Humidifier"
                  value={getRecordValue(row, "humidifierSystem")}
                />
              </DetailSection>
            </Col>
            <Col lg={6}>
              <DetailSection title="Usage Data">
                <DetailRow label="Data Period" value={getRecordValue(row, "dataPeriod")} />
                <DetailRow label="Date Summary" value={getRecordValue(row, "dateSummary")} />
                <DetailRow
                  label="Usage > 4h"
                  value={getRecordValue(row, "usageMoreThan4HoursPercent")}
                />
                <DetailRow
                  label="Avg Usage Hours"
                  value={getRecordValue(row, "averageUsageHours")}
                />
                <DetailRow label="Average AHI" value={getRecordValue(row, "averageAhi")} />
                <DetailRow
                  label="95% Pressure"
                  value={getRecordValue(row, "pressure95Percent")}
                />
                <DetailRow label="Average Leak" value={getRecordValue(row, "averageLeak")} />
                <DetailRow label="Leak Rate" value={getRecordValue(row, "leakRate")} />
                <DetailRow
                  label="Leak Assessment"
                  value={getRecordValue(row, "leakAssessment")}
                />
              </DetailSection>
            </Col>
          </Row>

          <Row>
            <Col lg={6}>
              <DetailSection title="Patient Outcome">
                <DetailRow label="ESS Score" value={getRecordValue(row, "essScore")} />
                <DetailRow
                  label="น้ำหนักปัจจุบัน"
                  value={getRecordValue(row, "currentWeight")}
                />
                <DetailRow
                  label="การเปลี่ยนแปลงน้ำหนัก"
                  value={getRecordValue(row, "weightChange")}
                />
                <DetailRow label="Wake Feeling" value={getRecordValue(row, "wakeFeeling")} />
                <DetailRow
                  label="Daytime Sleepiness"
                  value={getRecordValue(row, "daytimeSleepiness")}
                />
                <DetailRow label="Side Effects" value={sideEffects} />
                <DetailRow
                  label="อื่นๆ"
                  value={getRecordValue(row, "sideEffectsOther")}
                />
              </DetailSection>
            </Col>
            <Col lg={6}>
              <DetailSection title="Follow-up By And Files">
                <DetailRow
                  label="Technician"
                  value={getRecordValue(row, "technicianConsult")}
                />
                <DetailRow label="Doctor" value={getRecordValue(row, "doctorConsult")} />
                <DetailRow
                  label="Usage Reports"
                  value={usageReports.map((file) => file.originalFileName || file.name || file.savedFileName || "-")}
                />
                <DetailRow
                  label="Questionnaires"
                  value={questionnaires.map((file) => file.originalFileName || file.name || file.savedFileName || "-")}
                />
                <DetailRow label="Note" value={getRecordValue(row, "note")} />
              </DetailSection>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <>
      <PageTitle
        activeMenu="Follow-Up History"
        motherMenu="CPAP Follow-Up"
        motherLink="/cpap-followup-calendar"
      />
      <Row>
        <Col xl={12}>
          <Card>
            <Card.Header className="d-flex flex-wrap justify-content-between align-items-center">
              <div>
                <h4 className="mb-1">Follow-Up History</h4>
                <div className="text-muted small">
                  Found {filteredRecords.length} of {records.length} record(s)
                </div>
              </div>
              <div
                className="d-flex flex-wrap align-items-center"
                style={{ gap: 12 }}
              >
                <select
                  className="form-control"
                  style={{ width: 190 }}
                  value={actionFilter}
                  onChange={(e) => {
                    setActionFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="All Actions">All Actions</option>
                  <option value="doctor_action">Doctor Action</option>
                  <option value="update">Update</option>
                  <option value="create">Create</option>
                </select>
                <input
                  className="form-control"
                  style={{ width: 360, maxWidth: "100%" }}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search order, HN, patient, status"
                />
              </div>
            </Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <Table bordered hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 190 }}>Record</th>
                      <th style={{ width: 260 }}>Patient / Order</th>
                      <th style={{ width: 250 }}>Status Focus</th>
                      <th style={{ width: 230 }}>Therapy Focus</th>
                      <th style={{ width: 210 }}>Follow-up By</th>
                      <th style={{ width: 110 }}>Info</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          Loading history...
                        </td>
                      </tr>
                    ) : filteredRecords.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          No follow-up history found.
                        </td>
                      </tr>
                    ) : (
                      paginatedRecords.map((row) => {
                        const previousDecision = getPreviousDecisionRecord(row);

                        return (
                          <tr key={row.path}>
                            <td>
                              <div>{formatDateTime(row.recordAt)}</div>
                              <div className="small text-muted">
                                {row.recordBy || "-"}
                              </div>
                              <Badge variant={getHistoryActionBadge(row.action)}>
                                {getHistoryActionLabel(row.action)}
                              </Badge>
                            </td>
                            <td>
                              <div className="font-weight-bold">
                                {row.order || "-"}
                              </div>
                              <div className="small text-muted">
                                {getPatientName(row)} | {row.hn || "-"}
                              </div>
                              <div className="small text-muted">
                                {row.date || formatDateTime(row.start)}
                              </div>
                            </td>
                            <td>
                              <div className="font-weight-bold">
                                {row.status || "-"}
                              </div>
                              <div>{row.type || "-"}</div>
                              <div className="small text-muted">
                                Doctor Action:{" "}
                                {getDoctorActionLabel(row.doctorActionStatus)}
                              </div>
                              {previousDecision && (
                                <div className="small text-muted">
                                  Previous:{" "}
                                  {getDoctorActionLabel(
                                    previousDecision.doctorActionStatus,
                                  )}
                                </div>
                              )}
                            </td>
                            <td>
                              <div>AHI: {row.averageAhi || "-"}</div>
                              <div>Usage: {row.averageUsageHours || "-"}</div>
                              <div className="small text-muted">
                                Leak: {row.averageLeak || row.leakRate || "-"}
                              </div>
                              <div className="small text-muted">
                                Weight: {getRecordValue(row, "currentWeight") || "-"}
                              </div>
                              <div className="small text-muted">
                                Serial: {row.serialNumber || "-"}
                              </div>
                            </td>
                            <td>
                              <div>Tech: {row.technicianConsult || "-"}</div>
                              <div>Doctor: {row.doctorConsult || "-"}</div>
                              <div className="small text-muted">
                                Node: {row.node || "-"}
                              </div>
                            </td>
                            <td>
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={() => setSelectedRecord(row)}
                              >
                                See Info
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
              <div
                className="d-flex justify-content-between align-items-center flex-wrap px-3 py-2"
                style={{ borderTop: "1px solid #e9ecef", gap: 8 }}
              >
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Showing {startItem}-{endItem} of {filteredRecords.length}{" "}
                  record(s) · Page {activePage} of {totalPages} · {PAGE_SIZE}{" "}
                  items per page
                </div>

                {totalPages > 1 && (
                  <div className="d-flex align-items-center" style={{ gap: 6 }}>
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => goToPage(1)}
                      disabled={activePage === 1}
                      style={{ border: "1px solid #ced4da" }}
                    >
                      First
                    </Button>
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => goToPage(activePage - 1)}
                      disabled={activePage === 1}
                      style={{ border: "1px solid #ced4da" }}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => goToPage(activePage + 1)}
                      disabled={activePage === totalPages}
                      style={{ border: "1px solid #ced4da" }}
                    >
                      Next
                    </Button>
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => goToPage(totalPages)}
                      disabled={activePage === totalPages}
                      style={{ border: "1px solid #ced4da" }}
                    >
                      Last
                    </Button>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {renderDetailModal()}
    </>
  );
};

export default FollowupRecordHistory;
